import type { IFuseOptions } from 'fuse.js'
import { useCallback } from 'react'
import {
  type FuzzySearchResult,
  useFuzzySearch,
} from '@/components/admin/shared/hooks/use-fuzzy-search'
import type { Task } from '../types'

export const TASK_SEARCH_KEYS: NonNullable<IFuseOptions<Task>['keys']> = [
  { name: 'title', weight: 0.6 },
  { name: 'category', weight: 0.2 },
  { name: 'notes', weight: 0.2 },
]

export type TaskSearchResult = FuzzySearchResult<Task>

interface UseTaskSearchOptions {
  prioritizeSourceId?: string
}

export function useTaskSearch(
  tasks: Task[],
  options: UseTaskSearchOptions = {},
) {
  const { search: fuzzySearch, minSearchLength } = useFuzzySearch(
    tasks,
    TASK_SEARCH_KEYS,
  )
  const { prioritizeSourceId } = options

  // Tasks from the source being edited float to the top; relevance breaks ties.
  const search = useCallback(
    (query: string): TaskSearchResult[] => {
      const results = fuzzySearch(query)
      if (!prioritizeSourceId) return results

      return results.sort((a, b) => {
        const aMatch = a.item.sourceId === prioritizeSourceId
        const bMatch = b.item.sourceId === prioritizeSourceId
        if (aMatch && !bMatch) return -1
        if (!aMatch && bMatch) return 1
        return a.score - b.score
      })
    },
    [fuzzySearch, prioritizeSourceId],
  )

  return { search, minSearchLength }
}
